/**
 * ARIA Warranty BOM Panel
 *
 * Right-hand drawer on the Warranty Lookup page, laid out like a pivot table. The order is the
 * top level: one row per UNIQUE item on the order. A plus sign
 * (or a click on the row) expands the item's recipe in place: its components and the quantity of
 * each for ONE of the item (direct components only, so a recipe stays readable).
 * Mount it with key={record.id} so state resets per record.
 */

import { memo, useEffect, useRef, useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { X, Search, Package, Layers, ChevronLeft, ChevronRight, Plus, Minus } from 'lucide-react';
import { clsx } from 'clsx';
import type { BomItem, BomLine, ItemSpec, BomSource, BomSourceInfo, WarrantyBom, WarrantyBomItems, WarrantyHistoryResult } from '../../types';
import { isUnauthenticatedError } from '../../config/apollo';

const RECIPE_PAGE_SIZE = 100;
const NEST_INDENT_PX = 40;

export const WARRANTY_BOM_ITEMS_QUERY = gql`
  query WarrantyBomItems($warrantyId: ID!, $source: BomSource) {
    warrantyBomItems(warrantyId: $warrantyId, source: $source) {
      source
      key
      hasQuantities
      sources { source key status loadedAt }
      items { item description lines recipeRows status spec { series unitWidth unitHeight brickmould siteGlaze jambExtn jambSize lowProfileSill grillPattern grillFlag crating productFamily colorCode mullNumber boxPosition } }
    }
  }
`;

export const WARRANTY_BOM_QUERY = gql`
  query WarrantyBom($warrantyId: ID!, $source: BomSource, $item: String, $assembly: String, $maxLayer: Int, $limit: Int, $offset: Int) {
    warrantyBom(warrantyId: $warrantyId, source: $source, item: $item, assembly: $assembly, maxLayer: $maxLayer, limit: $limit, offset: $offset) {
      filteredCount
      lines { seq item description uom qty hasChildren mfgAttrs grillHorizontal grillVertical grillOrientation spec { series unitWidth unitHeight brickmould siteGlaze jambExtn jambSize lowProfileSill grillPattern grillFlag crating productFamily colorCode mullNumber boxPosition } }
    }
  }
`;

const SOURCE_LABEL: Record<BomSource, string> = {
  SYTELINE: 'SyteLine job BOM',
  ORACLE: 'Oracle BOM',
};

const STATUS_TEXT: Partial<Record<BomSourceInfo['status'] | BomItem['status'], string>> = {
  PENDING: 'queued, not loaded yet',
  IN_PROGRESS: 'loading now',
  FAILED: 'failed to load',
  EMPTY: 'no BOM',
  NOT_QUEUED: 'no BOM',
};

const qtyFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 });
const formatQty = (qty: number | null) => (qty == null ? '—' : qtyFormat.format(qty));

// The size and options Oracle holds on an item, as one readable line: "W 476 × H 1882.6 mm · Series 3550 · Brickmould Y".
const mm = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
function specLine(spec: ItemSpec | null | undefined): { size: string | null; options: string[] } {
  if (!spec) return { size: null, options: [] };
  const size =
    spec.unitWidth != null && spec.unitHeight != null ? `W ${mm.format(spec.unitWidth)} × H ${mm.format(spec.unitHeight)} mm` : null;
  const options = [
    spec.series && `Series ${spec.series}`,
    spec.colorCode && `Colour ${spec.colorCode}`,
    spec.brickmould && `Brickmould ${spec.brickmould}`,
    spec.siteGlaze && `Site glaze ${spec.siteGlaze}`,
    spec.jambSize && `Jamb ${spec.jambSize}`,
    spec.jambExtn && `Jamb ext ${spec.jambExtn}`,
    spec.lowProfileSill && `Low-profile sill ${spec.lowProfileSill}`,
    spec.grillPattern && spec.grillPattern !== 'None' && `Grill ${spec.grillPattern}`,
    spec.crating === 'Y' && 'Crating',
  ].filter((o): o is string => Boolean(o));
  return { size, options };
}

function SpecText({ spec }: { spec: ItemSpec | null | undefined }) {
  const { size, options } = specLine(spec);
  if (!size && options.length === 0) return null;
  return (
    <div className="text-xs text-gray-500 mt-0.5">
      {size && <span className="font-semibold text-gray-800">{size}</span>}
      {size && options.length > 0 && ' · '}
      {options.join(' · ')}
    </div>
  );
}

// SyteLine manufacturing attributes arrive as "CutLength=H:513:~V:~|HorizontalGrille=POH1:364~|". Show each as "Cut length H:513:~V:~".
export function parseMfgAttrs(raw: string | null | undefined): { label: string; value: string }[] {
  if (!raw) return [];
  return raw
    .split('|')
    .map((part) => {
      const at = part.indexOf('=');
      if (at <= 0) return null;
      const key = part.slice(0, at);
      const label = key.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
      const value = part.slice(at + 1).trim();
      // CUTINFO is on every cut line and says nothing a CSP needs
      if (!value || (key === 'InstructionType' && value === 'CUTINFO')) return null;
      return { label: label.charAt(0).toUpperCase() + label.slice(1), value };
    })
    .filter((a): a is { label: string; value: string } => a !== null);
}

function MfgAttrsText({ raw }: { raw: string | null | undefined }) {
  const attrs = parseMfgAttrs(raw);
  if (attrs.length === 0) return null;
  return (
    <div className="text-xs text-gray-600 mt-0.5">
      {attrs.map((a, i) => (
        <span key={a.label}>
          {i > 0 && ' · '}
          {a.label} <span className="font-semibold text-gray-800">{a.value}</span>
        </span>
      ))}
    </div>
  );
}

function GrillText({ line }: { line: Pick<BomLine, 'grillHorizontal' | 'grillVertical' | 'grillOrientation'> }) {
  const parts = [
    line.grillHorizontal && ['Grill horizontal', line.grillHorizontal],
    line.grillVertical && ['Grill vertical', line.grillVertical],
    line.grillOrientation && ['Grill orientation', line.grillOrientation],
  ].filter((p): p is string[] => Boolean(p));
  if (parts.length === 0) return null;
  return (
    <div className="text-xs text-gray-600 mt-0.5">
      {parts.map(([label, value], i) => (
        <span key={label}>
          {i > 0 && ' · '}
          {label} <span className="font-semibold text-gray-800">{value}</span>
        </span>
      ))}
      {(line.grillHorizontal || line.grillVertical) && <span className="text-gray-500"> (mm)</span>}
    </div>
  );
}

const lineLabel = (lines: number[]) =>
  lines.length === 0 ? '—' : lines.length > 3 ? `${lines[0]}–${lines[lines.length - 1]} (${lines.length})` : lines.join(', ');

interface RecipeScope {
  warrantyId: string;
  source: BomSource | null;
  // The item whose recipe this is. SyteLine keeps it for the whole tree and names the assembly below it;
  // Oracle opens a sub-assembly by making it the item.
  item: string;
}

// One component line. An assembly (it has components of its own) gets a plus sign that opens its
// components in place, one level at a time; the open state lives in the row.
const RecipeRow = memo(function RecipeRow({ line, scope, depth }: { line: BomLine; scope: RecipeScope; depth: number }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="align-top">
        <td className="w-8 px-2 py-1.5">
          {line.hasChildren && (
            <button
              aria-label={open ? `Hide components of ${line.item}` : `Show components of ${line.item}`}
              className="p-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
              onClick={() => setOpen(!open)}
            >
              {open ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
            </button>
          )}
        </td>
        <td className="px-2 py-1.5 font-medium text-gray-900 whitespace-nowrap">{line.item}</td>
        <td className="px-4 py-1.5 text-gray-700">
          {line.description || '—'}
          <SpecText spec={line.spec} />
          <MfgAttrsText raw={line.mfgAttrs} />
          <GrillText line={line} />
        </td>
        <td className="px-4 py-1.5 text-right tabular-nums text-gray-900">{formatQty(line.qty)}</td>
        <td className="px-2 py-1.5 text-gray-600">{line.uom || '—'}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} className="p-0">
            <ItemRecipe
              scope={scope.source === 'SYTELINE' ? scope : { ...scope, item: line.item }}
              assembly={scope.source === 'SYTELINE' ? line.item : undefined}
              depth={depth + 1}
            />
          </td>
        </tr>
      )}
    </>
  );
});

// A recipe (the components of an item, or of one assembly inside it), fetched when it is opened.
// SyteLine's top-level recipe is limited to direct components; long lists are paged.
function ItemRecipe({ scope, assembly, depth = 0 }: { scope: RecipeScope; assembly?: string; depth?: number }) {
  const [page, setPage] = useState(0);
  const { data, previousData, loading, error, refetch } = useQuery<{ warrantyBom: WarrantyBom }>(WARRANTY_BOM_QUERY, {
    variables: {
      warrantyId: scope.warrantyId,
      source: scope.source,
      item: scope.item,
      assembly,
      maxLayer: scope.source === 'SYTELINE' && !assembly ? 1 : undefined,
      limit: RECIPE_PAGE_SIZE,
      offset: page * RECIPE_PAGE_SIZE,
    },
  });
  const bom = data?.warrantyBom ?? previousData?.warrantyBom;

  if (error) {
    return isUnauthenticatedError(error) ? null : (
      <div className="px-6 py-3 text-sm text-red-500">
        Could not load this BOM.{' '}
        <button className="underline" onClick={() => refetch()}>Try again</button>
      </div>
    );
  }
  if (!bom) return <div className="px-6 py-3 text-sm text-gray-500">Loading components...</div>;

  const total = bom.filteredCount;
  const pageCount = Math.max(Math.ceil(total / RECIPE_PAGE_SIZE), 1);
  return (
    // Each nested level steps in by NEST_INDENT_PX from its parent (it sits inside the parent's table cell, so
    // the step is not multiplied by depth) and gets a heavier left rule, alternating tint, so a sub-assembly's
    // parts cannot be mistaken for its parent's.
    <div
      className={clsx(
        'border-t border-gray-100 py-2',
        depth === 0 ? 'bg-gray-50' : clsx('border-l-4', depth % 2 === 1 ? 'bg-white border-l-blue-300' : 'bg-blue-50/60 border-l-indigo-300'),
      )}
      style={{ marginLeft: depth === 0 ? 0 : NEST_INDENT_PX }}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase text-gray-500">
            <th className="w-8 px-2 py-1" />
            <th className="px-2 py-1">Component</th>
            <th className="px-4 py-1">Description</th>
            <th className="px-4 py-1 text-right">Qty per item</th>
            <th className="px-2 py-1">UOM</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {bom.lines.map((l, i) => (
            <RecipeRow key={`${l.seq}-${i}`} line={l} scope={scope} depth={depth} />
          ))}
        </tbody>
      </table>
      {total === 0 && <p className="px-6 py-2 text-sm text-gray-500">No components.</p>}
      {pageCount > 1 && (
        <div className="px-4 pt-2 flex items-center justify-center gap-2 text-sm text-gray-600">
          <button className="btn-secondary px-2 py-1" onClick={() => setPage(page - 1)} disabled={page === 0 || loading} aria-label="Previous components">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="tabular-nums">Components {(page * RECIPE_PAGE_SIZE + 1).toLocaleString()}–{(page * RECIPE_PAGE_SIZE + bom.lines.length).toLocaleString()} of {total.toLocaleString()}</span>
          <button className="btn-secondary px-2 py-1" onClick={() => setPage(page + 1)} disabled={page + 1 >= pageCount || loading} aria-label="Next components">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

interface Props {
  record: WarrantyHistoryResult;
  onClose: () => void;
}

export function WarrantyBomPanel({ record, onClose }: Props) {
  const [source, setSource] = useState<BomSource | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [itemFilter, setItemFilter] = useState('');
  const closeRef = useRef<HTMLButtonElement>(null);
  // The parent passes a fresh onClose every render; keep the effect below from re-running (and re-focusing) each time.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Keyboard: Escape closes; focus moves into the panel and returns to the row on close.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCloseRef.current();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, []);

  const { data, loading, error, refetch } = useQuery<{ warrantyBomItems: WarrantyBomItems }>(WARRANTY_BOM_ITEMS_QUERY, {
    variables: { warrantyId: record.id, source },
  });
  // Items belong to one source, so never show the previous source's list while another loads.
  const order = data?.warrantyBomItems;
  const items: BomItem[] = order?.items ?? [];
  const activeSource = order?.source ?? null;
  // With nothing loaded the server names no source: explain the queue entry that exists instead of "no items".
  const active = order?.sources.find((s) => s.source === activeSource) ?? (items.length === 0 ? order?.sources[0] : undefined);
  // The tabs stay put while a switch loads.
  const tabSources = useRef<BomSourceInfo[]>([]);
  if (order && order.sources.length > 1) tabSources.current = order.sources;

  const hasRecipe = (i: BomItem) => i.status === 'LOADED' && (i.recipeRows ?? 0) > 0;
  const isOpen = (i: BomItem) => expanded.has(i.item);
  // An order with one item opens it straight away (once per source); after that the user is in control.
  const autoOpened = useRef<string | null>(null);
  useEffect(() => {
    const key = `${activeSource}|${order?.key}`;
    if (items.length === 1 && hasRecipe(items[0]) && autoOpened.current !== key) {
      autoOpened.current = key;
      setExpanded(new Set([items[0].item]));
    }
  }, [order, items, activeSource]);
  const toggle = (i: BomItem) => {
    if (!hasRecipe(i)) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i.item)) next.delete(i.item);
      else next.add(i.item);
      return next;
    });
  };

  const shownItems = itemFilter
    ? items.filter((i) => `${i.item} ${i.description ?? ''}`.toLowerCase().includes(itemFilter.toLowerCase()))
    : items;
  const orderLabel = order?.key && (order.hasQuantities || activeSource === 'SYTELINE') ? `order ${order.key}` : 'this quote';

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" role="dialog" aria-modal="true" aria-label="BOM details">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 w-[94vw] max-w-[1700px] bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Layers className="w-5 h-5 text-gray-500" /> Bill of materials
            </h2>
            <p className="text-sm text-gray-700 truncate">
              {record.customerName}
              {record.address ? ` · ${record.address}` : ''}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {[
                record.serviceOrder && `Order ${record.serviceOrder}`,
                record.referencedPaNumber && `PA ${record.referencedPaNumber}`,
                record.sku && `Item ${record.sku}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <button ref={closeRef} onClick={onClose} aria-label="Close BOM panel" className="p-2 hover:bg-gray-200 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Source tabs */}
        {tabSources.current.length > 1 && (
          <div className="px-6 py-3 border-b border-gray-200 flex flex-wrap items-center gap-3">
            {tabSources.current.map((s: BomSourceInfo) => (
              <button
                key={s.source}
                onClick={() => {
                  setSource(s.source);
                  setExpanded(new Set());
                  setItemFilter('');
                }}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm border',
                  s.source === activeSource ? 'bg-blue-50 border-blue-300 text-blue-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                )}
              >
                {SOURCE_LABEL[s.source]} <span className="text-xs text-gray-500">({s.key})</span>
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {error ? (
            isUnauthenticatedError(error) ? null : (
              <div className="text-center py-12 text-red-500">
                <p>Could not load the bill of materials.</p>
                <button className="btn-secondary text-sm mt-3" onClick={() => refetch()}>Try again</button>
              </div>
            )
          ) : !order || loading ? (
            <div className="text-center py-12 text-gray-500">Loading bill of materials...</div>
          ) : order.sources.length === 0 && items.length === 0 ? (
            <div className="text-center py-12 text-gray-500 px-6">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p>No bill of materials is available for this record.</p>
              <p className="text-sm mt-1">
                Looked for {record.referencedPaNumber ? `PA order ${record.referencedPaNumber}` : 'no PA order'}
                {record.sku ? ` and item ${record.sku}` : ''}. It may not be loaded yet.
              </p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500 px-6">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p>
                {active && active.status !== 'LOADED'
                  ? `${SOURCE_LABEL[active.source]} ${active.key}: ${STATUS_TEXT[active.status] ?? active.status}.`
                  : 'This bill of materials has no items.'}
              </p>
            </div>
          ) : (
            <>
              <div className="px-6 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3 bg-gray-50">
                <span className="text-sm text-gray-600">
                  <span className="font-semibold">{items.length}</span> unique {items.length === 1 ? 'item' : 'items'} on {orderLabel}
                </span>
                {items.length > 8 && (
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" value={itemFilter} onChange={(e) => setItemFilter(e.target.value)} placeholder="Filter items" className="input pl-8 w-64" aria-label="Filter items" maxLength={100} />
                  </div>
                )}
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs uppercase text-gray-500">
                    <th className="w-10 px-3 py-2" />
                    <th className="px-2 py-2">Line</th>
                    <th className="px-4 py-2">Item</th>
                    <th className="px-4 py-2">Description</th>
                    <th className="px-4 py-2 text-right">Components</th>
                  </tr>
                </thead>
                {shownItems.map((i) => {
                  const open = isOpen(i);
                  const expandable = hasRecipe(i);
                  return (
                    <tbody key={i.item} className="border-b border-gray-100">
                      <tr
                        tabIndex={expandable ? 0 : -1}
                        onClick={() => toggle(i)}
                        onKeyDown={(e) => {
                          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            toggle(i);
                          }
                        }}
                        aria-expanded={expandable ? open : undefined}
                        className={clsx('align-top', expandable && 'cursor-pointer hover:bg-gray-50', i.item === record.sku && 'bg-blue-50')}
                      >
                        <td className="px-3 py-2">
                          {expandable && (
                            <button
                              aria-label={open ? `Hide BOM of ${i.item}` : `Show BOM of ${i.item}`}
                              className="p-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggle(i);
                              }}
                            >
                              {open ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                            </button>
                          )}
                        </td>
                        <td className="px-2 py-2 text-gray-700 whitespace-nowrap">{lineLabel(i.lines)}</td>
                        <td className="px-4 py-2 font-medium text-gray-900 whitespace-nowrap">{i.item}</td>
                        <td className="px-4 py-2 text-gray-700">
                          {i.description || '—'}
                          <SpecText spec={i.spec} />
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                          {expandable ? i.recipeRows : <span className="text-xs text-gray-500">{STATUS_TEXT[i.status] ?? i.status}</span>}
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={5} className="p-0">
                            <ItemRecipe scope={{ warrantyId: record.id, source: activeSource, item: i.item }} />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  );
                })}
              </table>
              {shownItems.length === 0 && <div className="text-center py-8 text-gray-500">No items match.</div>}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
